// Features: M004-F0730, M004-F0731, M004-F0732
// Tests: T-M004-0255, T-M004-0258
//
// The ONE place the billing-profile wire shape lives (Spec core-ledger-account-gateway 1.7.0: getLedgerBillingProfile /
// updateLedgerBillingProfile). Organisation-scoped, ledger_manage; the read is SAL 3, the write is SAL 4 with an Idempotency-Key.
// PROVISIONAL until the WebUI re-pins to Spec 1.7.0 (the proposal is Spec docs/20261007-website-billing-settings @ 72bfc17); the
// wire is validated by T-M004-0258 against the schemas (pinned once present, else the transcribed proposal).
//
// CLOSED SET (ACCT-BP-006, design 3.5): the page renders only the nine fields, the logo status, context and numbering. A response with
// any other member (an smtp_password, say) is a CONTRACT FAILURE: normaliseBillingProfile throws NOT_UNDERSTOOD and the page renders
// nothing from it.

import { FIELD_NAMES } from '../utils/billingProfileRules';

// WebUI #172 (M004-F0742): both operations go through Core's generic relay (CSLPT), POST /v1/orgs/:orgId/modules/ledger/messages with the
// closed envelope { relay_version: 1, type, payload }. Core !1034 adds the billing_profile rows with the typed routes' exact gates:
// organisation-scoped (no target_user_id), get needs ledger_read + ledger_manage (no SAL step), update needs both at SAL 4 under a key.
export const BILLING_PROFILE_OPERATIONS = Object.freeze({
  get: { op: 'billing_profile.get', access: 'read', key: 'none', target: 'none' },
  update: { op: 'billing_profile.update', access: 'write', key: 'required', target: 'none' },
});

const messagesPath = (orgId) => `/v1/orgs/${encodeURIComponent(orgId)}/modules/ledger/messages`;
const envelope = (type, payload) => ({ relay_version: 1, type, payload });

export const NOT_UNDERSTOOD = 'BILLING_PROFILE_NOT_UNDERSTOOD';
export class BillingProfileNotUnderstood extends Error {
  constructor(detail) { super(`The settings answer was not understood${detail ? ` (${detail})` : ''}`); this.code = NOT_UNDERSTOOD; }
}

const TOP = ['profile', 'effective', 'context', 'numbering', 'logo', 'warnings', 'version', 'updated_at'];
const EFFECTIVE = ['invoice_due_days', 'invoice_prefix'];
const CONTEXT = ['currency', 'vat_registered', 'org_code'];
const NUMBERING = ['next_seq', 'pad_width', 'next_invoice_number', 'first_invoice_issued', 'issued_invoice_count', 'first_invoice_number', 'last_invoice_number', 'prefix_change_requires_confirmation'];
const LOGO = ['exists', 'content_type', 'width', 'height', 'bytes'];
const WARNING_ALLOWED = ['code', 'severity', 'message', 'terms_days', 'due_days'];
const UPDATE_RESULT = ['billing_profile', 'changed', 'logo_changed', 'audit_id'];

const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);
const sameKeys = (value, keys, label) => {
  if (!isObject(value)) throw new BillingProfileNotUnderstood(label);
  const have = Object.keys(value);
  const extra = have.filter((k) => !keys.includes(k));
  const missing = keys.filter((k) => !have.includes(k));
  if (extra.length || missing.length) throw new BillingProfileNotUnderstood(label);
};

// Throws BillingProfileNotUnderstood unless `data` is EXACTLY the closed BillingProfile shape. Returns it unchanged.
export function normaliseBillingProfile(data) {
  sameKeys(data, TOP, 'billing_profile');
  sameKeys(data.profile, FIELD_NAMES, 'profile');
  sameKeys(data.effective, EFFECTIVE, 'effective');
  sameKeys(data.context, CONTEXT, 'context');
  sameKeys(data.numbering, NUMBERING, 'numbering');
  sameKeys(data.logo, LOGO, 'logo');
  if (!Array.isArray(data.warnings)) throw new BillingProfileNotUnderstood('warnings');
  data.warnings.forEach((w) => {
    if (!isObject(w) || Object.keys(w).some((k) => !WARNING_ALLOWED.includes(k)) || typeof w.message !== 'string' || typeof w.code !== 'string') throw new BillingProfileNotUnderstood('warning');
  });
  if (typeof data.version !== 'string' || !data.version) throw new BillingProfileNotUnderstood('version');
  return data;
}

export function normaliseUpdateResult(data) {
  sameKeys(data, UPDATE_RESULT, 'update result');
  if (!Array.isArray(data.changed) || data.changed.some((n) => !FIELD_NAMES.includes(n))) throw new BillingProfileNotUnderstood('changed');
  normaliseBillingProfile(data.billing_profile);
  return data;
}

// The typed-route answer shape, rebuilt from the relay answer { success, data: { payload: { data } } }: `data` is the Ledger's data.
// A relay answer without that member becomes NOT_UNDERSTOOD in unwrap, as a malformed typed answer did.
const typedAnswer = (response) => ({ ...response, data: { success: true, data: response?.data?.data?.payload?.data } });

const unwrap = (response) => {
  const body = response?.data;
  if (!isObject(body) || body.success !== true || !isObject(body.data)) throw new BillingProfileNotUnderstood('envelope');
  return body.data;
};

// Builds the CLOSED request body (design 3.1): only changed fields; logo only when changed; confirmation + reason only when asked.
export function buildUpdateBody({ expectedVersion, changes, logo, confirmPrefixChange, reason }) {
  const body = { expected_version: expectedVersion };
  if (changes && Object.keys(changes).length) body.changes = changes;
  if (logo) body.logo = logo;
  if (confirmPrefixChange) { body.confirm_prefix_change = true; body.reason = reason; }
  return body;
}

export function buildBillingProfileApi(http) {
  return {
    // M004-F0730: ledger_manage, SAL 3, no Idempotency-Key. Resolves to the validated BillingProfile.
    getBillingProfile: async (orgId) => normaliseBillingProfile(unwrap(typedAnswer(await http.post(messagesPath(orgId), envelope('billing_profile.get', {}))))),
    // M004-F0732: SAL 4 (Core answers its step-up challenge), Idempotency-Key required. Resolves to the validated UpdateBillingProfileResult.
    updateBillingProfile: async (orgId, params, idempotencyKey) => normaliseUpdateResult(unwrap(typedAnswer(await http.post(
      messagesPath(orgId), envelope('billing_profile.update', buildUpdateBody(params)), { headers: { 'Idempotency-Key': idempotencyKey } },
    )))),
  };
}

// ---- errors (design 3.4 and 1.2) --------------------------------------------------------------------------------------
export const errorBody = (error) => (isObject(error?.response?.data) ? error.response.data : {});
export const errorCode = (error) => { const c = errorBody(error).error; return typeof c === 'string' ? c : (c && c.code) || ''; };
export const errorMessage = (error) => { const b = errorBody(error); return (typeof b.message === 'string' && b.message) || (b.error && b.error.message) || ''; };
export const errorDetails = (error) => (isObject(errorBody(error).details) ? errorBody(error).details : {});

export const READ_CONDITIONS = Object.freeze({
  TUNNEL_NOT_CONNECTED: 'The Ledger node is offline, so settings cannot be shown or saved.',
  LEDGER_NODE_NOT_LINKED: 'No Ledger node is linked to this organisation.',
  LEDGER_ACCOUNT_OPERATION_UNSUPPORTED: 'The Ledger has not been updated to serve billing settings yet.',
});
export const DENIED_SENTENCE = 'You need Ledger manager access in this organisation to see billing settings.';
export const LOGO_SENTENCE = 'Use a JPEG under 2 MB.';
export const STALE_SENTENCE = 'Someone else changed these settings. Your edits are kept below; the saved values are shown beside them.';

export const PROFILE_ERROR_CODES = Object.freeze([
  'LEDGER_BILLING_PROFILE_INVALID', 'LEDGER_CFG_INVOICE_PREFIX_INVALID', 'LEDGER_CFG_INVOICE_DUE_DAYS_INVALID',
  'LEDGER_BILLING_PROFILE_FIELD_NOT_EDITABLE', 'LEDGER_LOGO_INVALID', 'LEDGER_LOGO_EMPTY', 'LEDGER_LOGO_TOO_LARGE', 'LEDGER_REASON_REQUIRED',
]);
export const FIELD_FOR_CODE = Object.freeze({
  LEDGER_CFG_INVOICE_PREFIX_INVALID: 'invoice_prefix',
  LEDGER_CFG_INVOICE_DUE_DAYS_INVALID: 'invoice_due_days',
  LEDGER_LOGO_INVALID: 'logo', LEDGER_LOGO_EMPTY: 'logo', LEDGER_LOGO_TOO_LARGE: 'logo',
  LEDGER_REASON_REQUIRED: 'reason',
});
